import { useState } from 'react'
import { useSnackbar } from 'notistack'
import { t } from '@lingui/macro'

import { useGetAdminEstablishmentQuery } from '@/core/services/admin'
import {
  useAddTrustedActorMutation,
  useListTrustedActorsQuery,
  useRemoveTrustedActorMutation,
  useUpdateTrustedActorMutation,
} from '@/core/services/establishment'
import type { CreateTrustedActorDto, TrustedActor } from '@/core/models/trusted-actor.model'

const PAGE_SIZE = 7

export function useTrustedActors() {
  const { enqueueSnackbar } = useSnackbar()
  const { data: establishment } = useGetAdminEstablishmentQuery()
  const establishmentId = establishment?._id

  const [page, setPage] = useState(1) // 1-indexed for MUI Pagination
  const skip = (page - 1) * PAGE_SIZE

  const {
    data: pageData,
    isLoading,
    isFetching,
    error,
  } = useListTrustedActorsQuery({ establishmentId: establishmentId!, limit: PAGE_SIZE, skip }, { skip: !establishmentId })

  const actors = pageData?.items ?? []
  const totalCount = pageData?.totalCount ?? 0
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))

  const [addActor, { isLoading: isAdding }] = useAddTrustedActorMutation()
  const [updateActor, { isLoading: isUpdating }] = useUpdateTrustedActorMutation()
  const [removeActor, { isLoading: isRemoving }] = useRemoveTrustedActorMutation()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingActor, setEditingActor] = useState<TrustedActor | null>(null)
  const [actorToDelete, setActorToDelete] = useState<TrustedActor | null>(null)

  const handleOpenAdd = () => {
    setEditingActor(null)
    setDialogOpen(true)
  }
  const handleOpenEdit = (actor: TrustedActor) => {
    setEditingActor(actor)
    setDialogOpen(true)
  }
  const handleCloseDialog = () => {
    setDialogOpen(false)
    setEditingActor(null)
  }

  const handleSubmit = async (dto: CreateTrustedActorDto) => {
    if (!establishmentId) return
    try {
      if (editingActor) {
        await updateActor({ establishmentId, actorId: editingActor._id, dto }).unwrap()
        enqueueSnackbar(t`Trusted actor updated successfully`, { variant: 'success' })
      } else {
        await addActor({ establishmentId, dto }).unwrap()
        enqueueSnackbar(t`Trusted actor added successfully`, { variant: 'success' })
      }
      handleCloseDialog()
    } catch (err: any) {
      const message = err?.data?.message
      if (typeof message === 'string' && /already exists/i.test(message)) {
        enqueueSnackbar(t`An actor with this name already exists`, { variant: 'error' })
      } else if (editingActor) {
        enqueueSnackbar(t`Failed to update trusted actor`, { variant: 'error' })
      } else {
        enqueueSnackbar(t`Failed to add trusted actor`, { variant: 'error' })
      }
    }
  }

  const handleConfirmRemove = async () => {
    if (!establishmentId || !actorToDelete) return
    try {
      await removeActor({ establishmentId, actorId: actorToDelete._id }).unwrap()
      enqueueSnackbar(t`Trusted actor removed`, { variant: 'success' })
      setActorToDelete(null)
      // If we just removed the last row on this page, step back a page
      if (actors.length === 1 && page > 1) setPage(page - 1)
    } catch {
      enqueueSnackbar(t`Failed to remove trusted actor`, { variant: 'error' })
    }
  }

  const rangeStart = totalCount === 0 ? 0 : skip + 1
  const rangeEnd = Math.min(skip + actors.length, totalCount)

  return {
    actors,
    actorToDelete,
    dialogOpen,
    editingActor,
    error,
    establishmentId,
    handleCloseDialog,
    handleConfirmRemove,
    handleOpenAdd,
    handleOpenEdit,
    handleSubmit,
    isAdding,
    isLoading: isLoading || isFetching,
    isRemoving,
    isUpdating,
    page,
    pageSize: PAGE_SIZE,
    rangeEnd,
    rangeStart,
    setActorToDelete,
    setPage,
    totalCount,
    totalPages,
  }
}
